import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { Handshake, MousePointerClick, PartyPopper, Sparkles, CheckCircle2 } from "lucide-react";
import { useSnapshot } from "../hooks/useSnapshot";
import { useClock } from "../hooks/useClock";
import { useWakeLock } from "../hooks/useWakeLock";
import { useRotation } from "../hooks/useRotation";
import { useSound } from "../hooks/useSound";
import { useHistoryMode } from "../hooks/useHistoryMode";
import { useStage } from "../hooks/useStage";
import { TopBar } from "../components/TopBar";
import { EuropeMap } from "../components/EuropeMap";
import { MapLegend } from "../components/MapLegend";
import { RotationControls } from "../components/RotationControls";
import { KPIPanel } from "../components/KPIPanel";
import { PartnersStrip } from "../components/PartnersStrip";
import { TopCountriesTable } from "../components/TopCountriesTable";
import { LatestClients } from "../components/LatestClients";
import { DailyChart } from "../components/DailyChart";
import { PRODUCT_LABEL, PRODUCT_ORDER, UNASSIGNED_CODE, countryName, fmt, fmtDate } from "../lib/constants";
import { useAuth } from "../hooks/useAuth";
import { ViewerGate } from "../components/ViewerGate";
import { HINT, HowWeCountDialog } from "../components/HowWeCount";
import { useDayMode } from "../hooks/useDayMode";
import { useMediaQuery } from "../hooks/useMediaQuery";
import { useInstallPrompt } from "../hooks/useInstallPrompt";
import { DayPanel } from "../components/DayPanel";
import { HygienePanel } from "../components/HygienePanel";
import { NotCountedPanel } from "../components/NotCountedPanel";
import { PaymentsPanel } from "../components/PaymentsPanel";
import { SalesMixPanel } from "../components/SalesMixPanel";
import { shortName } from "../components/OwnerBreakdown";
import { useTheme } from "../hooks/useTheme";
import { buildRangeView, fmtRangeCountry, singleDay, todayIso } from "../lib/dayView";

const DELAYED_MS = 15 * 60 * 1000;
const RELOAD_MS = 10 * 60 * 1000;
const HIGHLIGHT_MS = 30000;

const useTimedSet = () => {
  const [set, setSet] = useState(() => new Set());
  const add = useCallback((key) => {
    setSet((s) => new Set(s).add(key));
    setTimeout(() => setSet((s) => { const n = new Set(s); n.delete(key); return n; }), HIGHLIGHT_MS);
  }, []);
  return [set, add];
};

const useFullscreen = () => {
  const [on, setOn] = useState(!!document.fullscreenElement);
  useEffect(() => {
    const h = () => setOn(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", h);
    return () => document.removeEventListener("fullscreenchange", h);
  }, []);
  const toggle = () => (document.fullscreenElement ? document.exitFullscreen?.() : document.documentElement.requestFullscreen?.());
  return [on, toggle];
};

export default function Wallboard() {
  const [roles, , signOut, logout] = useAuth();
  const { data, error } = useSnapshot(!!roles?.viewer, signOut);
  const now = useClock();
  const [isFullscreen, toggleFullscreen] = useFullscreen();
  const [countryHighlights, highlightCountry] = useTimedSet();
  const [clientHighlights, highlightClient] = useTimedSet();
  const prevIds = useRef(null);
  const sound = useSound();
  const [historyMode, toggleHistory] = useHistoryMode();
  const [helpOpen, setHelpOpen] = useState(false);
  const dayMode = useDayMode();
  const stage = useStage();
  // phones + portrait tablets: compact country table (initials for the owner, only sold products) so it fits without scrolling
  const narrow = useMediaQuery("(max-width: 1023px)");
  const installer = useInstallPrompt();
  const theme = useTheme();
  const [hygieneOpen, setHygieneOpen] = useState(false);
  const [notCountedOpen, setNotCountedOpen] = useState(false);
  const [paymentsOpen, setPaymentsOpen] = useState(false);
  const [mixOpen, setMixOpen] = useState(false);
  const [notCountedCountry, setNotCountedCountry] = useState(null);
  const openNotCounted = useCallback((code = null) => {
    setNotCountedCountry(code || null);
    setNotCountedOpen(true);
  }, []);
  useEffect(() => {
    const t = setTimeout(() => window.location.reload(), RELOAD_MS);
    return () => clearTimeout(t);
  }, []);
  useWakeLock();

  const baseView = data?.snapshot?.views?.[historyMode];
  // Day / range view (level 2): the whole board filtered to a date range, computed client-side from recent_won
  const snapshot = useMemo(() => (baseView && dayMode.boardRange ? buildRangeView(baseView, dayMode.boardRange) : baseView), [baseView, dayMode.boardRange]);
  const allView = data?.snapshot?.views?.all;
  const countFrom = data?.snapshot?.count_from;
  const trackingFrom = data?.snapshot?.tracking_from;
  const ownersAvailable = !!data?.snapshot?.owners_available;
  const targets = data?.snapshot?.targets;
  // won since the cutoff but not on a Venue company – not on the board, waiting for the deal owner (shown to everyone)
  const notCounted = data?.snapshot?.not_counted || null;
  // map ghost "+n" and the Pending table column – not in day/range mode (the chip is hidden there too)
  const pendingByCountry = dayMode.boardRange ? null : notCounted?.by_country || null;
  // celebration for everyone: a not-counted deal got fixed in HubSpot and its venue is counted now
  const prevFixedIds = useRef(null);
  const panelOpenRef = useRef(false);
  panelOpenRef.current = notCountedOpen;
  const fixedVenueIds = useMemo(() => new Set((notCounted?.fixed_today || []).map((f) => f.venue_id)), [notCounted]);
  useEffect(() => {
    const fixed = notCounted?.fixed_today;
    if (!Array.isArray(fixed)) return;
    if (prevFixedIds.current) {
      fixed.filter((f) => !prevFixedIds.current.has(f.id)).forEach((f) => {
        highlightCountry(f.country);
        if (panelOpenRef.current) return; // the open panel shows the "fixed today" chip and list itself
        toast.custom(
          () => (
            <div data-testid="not-counted-fixed-toast" className="bg-panel text-ink rounded-xl border border-panel-border border-l-4 border-l-live shadow-xl flex items-center gap-3 px-4 py-3 min-w-[320px]">
              <span className="w-9 h-9 rounded-full flex items-center justify-center bg-live/10 text-live"><CheckCircle2 className="w-5 h-5" /></span>
              <div className="min-w-0">
                <div className="eyebrow">Now counted · CRM fixed</div>
                <div className="font-bold text-ink truncate">{f.venue}</div>
                <div className="text-sm text-slate-500">fixed by {shortName(f.owner || "")}{f.country && f.country !== UNASSIGNED_CODE ? ` · ${countryName(f.country)}` : ""}</div>
              </div>
            </div>
          ),
          { duration: 10000 },
        );
      });
    }
    prevFixedIds.current = new Set(fixed.map((f) => f.id));
  }, [notCounted, highlightCountry]);
  // CRM hygiene is for signed-in people only (Google / admin) – display screens never get the summary
  const staff = !!roles?.viewer && roles.method !== "display";
  const hygieneSummary = staff ? data?.snapshot?.hygiene_summary || null : null;
  // small celebration for signed-in people when CRM findings disappear after a refresh (never on display screens)
  const prevFixed = useRef(null);
  useEffect(() => {
    const fixed = hygieneSummary?.fixed_today;
    if (typeof fixed !== "number") return;
    if (prevFixed.current !== null && fixed > prevFixed.current) {
      const n = fixed - prevFixed.current;
      toast.custom(
        () => (
          <div data-testid="hygiene-fixed-toast" className="bg-panel text-ink rounded-xl border border-panel-border border-l-4 border-l-live shadow-xl flex items-center gap-3 px-4 py-3 min-w-[320px]">
            <span className="w-9 h-9 rounded-full flex items-center justify-center bg-live/10 text-live"><PartyPopper className="w-5 h-5" /></span>
            <div className="min-w-0">
              <div className="eyebrow">CRM hygiene</div>
              <div className="font-bold text-ink">{n === 1 ? "1 finding fixed" : `${n} findings fixed`} since the last refresh</div>
              <div className="text-sm text-slate-500">{fmt(fixed)} fixed today · {fmt(hygieneSummary.total)} still open</div>
            </div>
          </div>
        ),
        { duration: 8000 },
      );
    }
    prevFixed.current = fixed;
  }, [hygieneSummary]);
  // Country row click: the panel for that country over the period the table currently shows (since cutoff, within the window)
  const periodRange = () => {
    const today = todayIso();
    const from = [baseView?.recent_from, historyMode === "since" ? countFrom : null].filter(Boolean).sort().pop() || today;
    return { from, to: today };
  };
  const openCountry = (code) => dayMode.setPanelRange({ ...periodRange(), country: code });
  // Partners strip: the same period (or the board's day/range), partner-sourced venues only, optionally one country
  const openPartners = (code) => dayMode.setPanelRange({ ...(dayMode.boardRange || periodRange()), partner: true, ...(code ? { country: code } : {}) });
  const views = useMemo(() => ["all", ...PRODUCT_ORDER.filter((p) => (snapshot?.products?.[p] || 0) > 0)], [snapshot]);
  const rotation = useRotation(views);
  const viewCounts = useMemo(() => ({ all: snapshot?.totals?.total || 0, ...(snapshot?.products || {}) }), [snapshot]);

  useEffect(() => {
    const latest = allView?.latest_won;
    if (!latest) return;
    if (prevIds.current) {
      const fresh = latest.filter((c) => !prevIds.current.has(c.id));
      // no chime for venues that only became counted through a CRM fix (celebrated by the silent "now counted" toast)
      if (fresh.some((c) => !fixedVenueIds.has(c.id))) sound.chime();
      fresh.forEach((c) => {
        highlightCountry(c.country);
        highlightClient(c.id);
        if (fixedVenueIds.has(c.id)) return; // the "now counted · CRM fixed" toast already celebrates this venue
        toast.custom(
          () => (
            <div data-testid="new-client-toast" className={`bg-panel text-ink rounded-xl border border-panel-border border-l-4 shadow-xl flex items-center gap-3 px-4 py-3 min-w-[320px] ${c.partner ? "border-l-amber-400" : "border-l-brand"}`}>
              <span className={`w-9 h-9 rounded-full flex items-center justify-center ${c.partner ? "bg-amber-100 text-amber-700" : "bg-brand/10 text-brand"}`}>{c.partner ? <Handshake className="w-5 h-5" /> : <Sparkles className="w-5 h-5" />}</span>
              <div className="min-w-0">
                <div className="eyebrow">{c.partner ? `New venue · via ${c.partner}` : "New venue"}</div>
                <div className="font-bold text-ink truncate">{c.name}</div>
                <div className="text-sm text-slate-500">{c.merchant && c.merchant !== c.name ? `${c.merchant} · ` : ""}{countryName(c.country)}{c.products?.length ? ` · ${c.products.map((p) => PRODUCT_LABEL[p] || p).join(", ")}` : ""}</div>
              </div>
            </div>
          ),
          { duration: HIGHLIGHT_MS },
        );
      });
    }
    prevIds.current = new Set(latest.map((c) => c.id));
  }, [allView, highlightCountry, highlightClient, sound, fixedVenueIds]);

  const lastSuccess = data?.refresh?.last_success_at ? new Date(data.refresh.last_success_at) : null;
  const nextRefresh = data?.refresh?.next_refresh_at ? new Date(data.refresh.next_refresh_at) : null;
  const status = !lastSuccess ? "waiting" : now - lastSuccess > DELAYED_MS ? "delayed" : "live";

  if (roles === null) return <div className="min-h-screen bg-page" data-testid="auth-checking" />;
  if (!roles.viewer) return <ViewerGate displayInvalid={!!roles.displayInvalid} />;

  const board = (
    <div className="min-h-screen tv:min-h-0 tv:h-full overflow-x-hidden tv:overflow-hidden bg-page text-ink flex flex-col gap-3 tv:gap-[15px] p-3 tv:p-[15px] theme-fade" data-testid="wallboard" data-theme={theme.theme}>
      <TopBar
        status={status}
        lastSuccess={lastSuccess}
        nextRefresh={nextRefresh}
        now={now}
        isFullscreen={isFullscreen}
        onFullscreen={toggleFullscreen}
        soundOn={sound.enabled}
        onSoundToggle={sound.toggle}
        historyMode={historyMode}
        onHistoryToggle={toggleHistory}
        countFrom={countFrom}
        user={roles}
        onSignOut={logout}
        onHelp={() => setHelpOpen(true)}
        onDetails={() => dayMode.setPanelRange(singleDay(todayIso()))}
        onInstall={installer.canInstall ? installer.install : undefined}
        hygiene={hygieneSummary}
        onHygiene={staff && hygieneSummary ? () => setHygieneOpen(true) : undefined}
        theme={theme}
      />
      {error && !snapshot && (
        <div className="panel px-4 py-3 text-sm text-slate-700 flex items-center gap-3" data-testid="snapshot-error">
          <span className="w-2.5 h-2.5 rounded-full bg-delayed live-dot shrink-0" />
          {error}
        </div>
      )}
      <main className="flex-1 min-h-0 grid grid-cols-1 tv:grid-cols-[minmax(0,7fr)_minmax(0,5fr)] gap-3 tv:gap-[15px]">
        <section className="panel panel-map relative flex flex-col h-[88vw] max-h-[560px] sm:h-[64vw] tv:h-auto tv:max-h-none tv:min-h-0 overflow-hidden fade-up" data-testid="map-panel">
          <div className="p-4 tv:p-[19px] pb-0 tv:pb-0">
            <RotationControls views={views} active={rotation.active} pinned={rotation.pinned} paused={rotation.paused} progress={rotation.progress} counts={viewCounts} onPause={() => rotation.setPaused((p) => !p)} onPin={rotation.pin} />
            {snapshot && (
              <div className="sm:hidden mt-1.5 flex items-center gap-1.5 text-[11px] font-semibold text-slate-500" data-testid="map-click-hint-phone">
                <MousePointerClick className="w-3.5 h-3.5 text-brand" /> Tap a country for details
              </div>
            )}
          </div>
          <div className="flex-1 min-h-0 relative">
            <EuropeMap countries={snapshot?.countries} view={rotation.active} highlights={countryHighlights} onCountryClick={openCountry} showOwners={ownersAvailable} dark={theme.dark} pending={pendingByCountry} onPendingClick={openNotCounted} />
            <div className="absolute left-4 right-4 bottom-3 tv:left-[19px] tv:right-[19px] tv:bottom-[15px] flex items-end justify-between gap-2 pointer-events-none">
              <div className="pointer-events-auto"><MapLegend dark={theme.dark} /></div>
              {snapshot && (
                <div className="pointer-events-auto hidden sm:inline-flex items-center gap-1.5 tv:gap-[8px] rounded-full bg-panel/95 border border-panel-border shadow-sm px-2.5 py-1 tv:px-[14px] tv:py-[7px] text-[11px] tv:text-[14px] font-semibold text-slate-700 whitespace-nowrap" data-testid="map-click-hint">
                  <MousePointerClick className="w-3.5 h-3.5 tv:w-[17px] tv:h-[17px] text-brand" /> Click a country for details
                </div>
              )}
            </div>
            {!snapshot && (
              <div className="absolute inset-0 flex items-center justify-center" data-testid="map-waiting">
                <div className="panel px-5 py-3 text-sm text-slate-600">
                  {data?.refresh?.last_error ? `Waiting for first successful refresh · ${data.refresh.last_error}` : "Fetching HubSpot data · first snapshot in progress…"}
                </div>
              </div>
            )}
          </div>
        </section>
        <aside className="flex flex-col gap-3 tv:gap-[15px] min-h-0">
          <KPIPanel totals={snapshot?.totals} newClients={snapshot?.new_clients} targets={targets} historyMode={historyMode} countFrom={countFrom} onHelp={() => setHelpOpen(true)} range={dayMode.boardRange} onExitDay={dayMode.exitBoard} notCounted={notCounted} onNotCounted={() => openNotCounted()} payments={snapshot?.payments} onPayments={() => setPaymentsOpen(true)} salesMix={snapshot?.sales_mix} onSalesMix={() => setMixOpen(true)} />
          {snapshot && <PartnersStrip view={snapshot} range={dayMode.boardRange} onOpen={() => openPartners()} onCountry={openPartners} />}
          <div className="tv:flex-[14] min-h-0 flex flex-col tv:h-auto">
            <TopCountriesTable countries={snapshot?.countries} products={snapshot?.products} activeView={rotation.active} onPinProduct={rotation.pin} highlights={countryHighlights} historyMode={dayMode.boardRange ? "since" : historyMode} onCountryClick={openCountry} compact={narrow && !stage.tv} showOwners={ownersAvailable} maxRows={stage.tv ? 7 : 10} pending={pendingByCountry} onPendingClick={openNotCounted} />
          </div>
          <div className="tv:flex-[8] min-h-0 grid grid-cols-1 tv:grid-cols-[minmax(0,3fr)_minmax(0,2fr)] gap-3 tv:gap-[15px]">
            <div className="tv:h-auto min-h-0 flex flex-col">
              <LatestClients
                latest={snapshot?.latest_won}
                highlights={clientHighlights}
                title={dayMode.boardRange ? `Venues onboarded ${fmtRangeCountry(dayMode.boardRange)}` : "Latest new venues"}
                emptyText={dayMode.boardRange ? "No venues onboarded in this period" : "No new venues in this view yet"}
              />
            </div>
            <div className="h-60 sm:h-72 tv:h-auto min-h-0 flex flex-col"><DailyChart series={snapshot?.daily_series} onSelectDay={(d) => dayMode.setPanelRange(singleDay(d))} selectedRange={dayMode.boardRange} dark={theme.dark} /></div>
          </div>
        </aside>
      </main>
    </div>
  );
  const help = <HowWeCountDialog open={helpOpen} onOpenChange={setHelpOpen} countFrom={countFrom} trackingFrom={trackingFrom} targets={targets} installer={installer} />;
  const dayPanel = <DayPanel view={baseView} range={dayMode.panelRange} onChangeRange={dayMode.setPanelRange} onClose={() => dayMode.setPanelRange(null)} onShowOnBoard={dayMode.showOnBoard} countFrom={countFrom} ownersPeriod={historyMode === "all" ? "all time" : `since ${fmtDate(countFrom)}`} showOwners={ownersAvailable} />;
  const hygienePanel = staff ? <HygienePanel open={hygieneOpen} onOpenChange={setHygieneOpen} generatedAt={hygieneSummary?.generated_at} isAdmin={!!roles.admin} countFrom={trackingFrom || countFrom} /> : null;
  const notCountedPanel = <NotCountedPanel open={notCountedOpen} onOpenChange={setNotCountedOpen} data={notCounted} initialCountry={notCountedCountry} />;
  const paymentsPanel = <PaymentsPanel open={paymentsOpen} onOpenChange={setPaymentsOpen} payments={snapshot?.payments} since={historyMode === "all" ? null : countFrom} rangeLabel={dayMode.boardRange ? `· ${fmtRangeCountry(dayMode.boardRange)}` : historyMode === "all" ? "· all time" : null} />;

  const mixPanel = <SalesMixPanel open={mixOpen} onOpenChange={setMixOpen} mix={snapshot?.sales_mix} since={historyMode === "all" ? null : countFrom} rangeLabel={dayMode.boardRange ? `· ${fmtRangeCountry(dayMode.boardRange)}` : historyMode === "all" ? "· all time" : null} />;

  if (!stage.tv) return <>{board}{help}{dayPanel}{hygienePanel}{notCountedPanel}{paymentsPanel}{mixPanel}</>;
  return (
    <>
      <div className="tv-viewport" data-testid="tv-viewport">
        <div className="tv-stage" data-testid="tv-stage" style={{ transform: `scale(${stage.scale})`, left: stage.left, top: stage.top }}>
          {board}
        </div>
      </div>
      {help}
      {dayPanel}
      {hygienePanel}
      {notCountedPanel}
      {paymentsPanel}
      {mixPanel}
    </>
  );
}
